<template>
  <div>
    <el-alert
      v-if="!chartData || chartData.length === 0"
      title="暂无数据"
      type="info"
      show-icon
      style="margin-top: 20px"
    />
    <div ref="chartContainer" style="width: 100%; height: 500px"></div>
  </div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "LineChart",
  props: {
    // 从接口获取的原始数据
    apiData: {
      type: Object,
      default: () => ({}),
    },
    // 图表标题
    title: {
      type: String,
      default: "数据趋势图",
    },
    // Y轴名称
    yAxisName: {
      type: String,
      default: "金额",
    },
    // 是否显示加载动画
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartInstance: null,
      chartData: [],
    };
  },
  watch: {
    // 监听apiData变化，自动更新图表
    apiData: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.processData(newVal);
          this.renderChart();
        }
      },
      immediate: true,
      deep: true,
    },
    loading(newVal) {
      if (this.chartInstance) {
        newVal
          ? this.chartInstance.showLoading()
          : this.chartInstance.hideLoading();
      }
    },
  },
  mounted() {
    this.initChart();
    if (Object.keys(this.apiData).length > 0) {
      this.processData(this.apiData);
      this.renderChart();
    }
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  methods: {
    // 初始化图表
    initChart() {
      if (!this.$refs.chartContainer) return;

      this.chartInstance = echarts.init(this.$refs.chartContainer);

      // 响应窗口大小变化
      window.addEventListener("resize", this.handleResize);

      // 显示加载动画
      if (this.loading) {
        this.chartInstance.showLoading();
      }
    },

    // 处理接口数据
    processData(rawData) {
      // 将对象转换为数组并排序
      const dataArray = Object.entries(rawData).map(([date, value]) => ({
        date,
        value,
      }));

      // 按日期排序
      dataArray.sort((a, b) => new Date(a.date) - new Date(b.date));

      this.chartData = dataArray;
    },

    // 渲染图表
    renderChart() {
      if (!this.chartInstance || this.chartData.length === 0) return;

      const dates = this.chartData.map((item) => item.date);
      const values = this.chartData.map((item) => item.value);

      const option = {
        title: {
          text: this.title,
          left: "center",
          textStyle: {
            fontSize: 18,
            fontWeight: "bold",
          },
        },
        tooltip: {
          trigger: "axis",
          formatter: function (params) {
            const param = params[0];
            return `${param.axisValue}<br/>${param.marker} ${param.seriesName}: ${param.value}`;
          },
          backgroundColor: "rgba(50, 50, 50, 0.7)",
          borderColor: "#333",
          textStyle: {
            color: "#fff",
          },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "10%",
          top: "15%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: dates,
          axisLabel: {
            rotate: 45, // 日期标签旋转45度，防止重叠
            interval: Math.ceil(dates.length / 10), // 自动调整显示间隔
          },
          axisLine: {
            lineStyle: {
              color: "#999",
            },
          },
        },
        yAxis: {
          type: "value",
          name: this.yAxisName,
          nameTextStyle: {
            fontSize: 14,
          },
          axisLine: {
            lineStyle: {
              color: "#999",
            },
          },
          splitLine: {
            lineStyle: {
              type: "dashed",
              color: "#eee",
            },
          },
        },
        series: [
          {
            name: "数据值",
            type: "line",
            data: values,
            smooth: true, // 平滑曲线
            symbol: "circle", // 数据点样式
            symbolSize: 8,
            itemStyle: {
              color: "#409EFF", // Element UI 主色
            },
            lineStyle: {
              width: 3,
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "rgba(64, 158, 255, 0.3)" },
                { offset: 1, color: "rgba(64, 158, 255, 0.1)" },
              ]),
            },
            markPoint: {
              data: [
                { type: "max", name: "最大值" },
                { type: "min", name: "最小值" },
              ],
              symbolSize: 40,
              label: {
                fontSize: 12,
              },
            },
            markLine: {
              data: [{ type: "average", name: "平均值" }],
              label: {
                fontSize: 12,
              },
            },
          },
        ],
        dataZoom: [
          {
            type: "inside", // 内置型数据区域缩放
            start: 0,
            end: 100,
          },
          {
            type: "slider", // 滑动条型数据区域缩放
            show: true,
            start: 0,
            end: 100,
            bottom: 10,
            height: 20,
          },
        ],
      };

      this.chartInstance.setOption(option);
      this.chartInstance.hideLoading();
    },

    // 处理窗口大小变化
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },

    // 更新数据的方法（可以从外部调用）
    updateData(newData) {
      this.processData(newData);
      this.renderChart();
    },
  },
};
</script>